import {
  addIds,
  formatQuery,
  getTranslatedText,
  QueryBuilder,
  renamePropertyInRuleGroup,
  OPERATOR_COMPONENT_MAP,
  ENTITY_MAP,
  getEntityAttributes,
  checkInactiveField
} from '@nile/components';
import { includes, pick } from 'lodash';
import { produce } from 'immer';
import nanoid from 'nanoid';
import React, { useEffect, useState } from 'react';
import { injectIntl } from 'react-intl';

const appendProperties = (queryToFormat) => {
  let query = { ...queryToFormat };
  query = renamePropertyInRuleGroup(query, 'joinOperator', 'combinator');
  query = renamePropertyInRuleGroup(query, 'attribute', 'field');
  query = renamePropertyInRuleGroup(query, 'operands', 'rules');
  query = addIds(query);
  return query;
};

function RuleBuilder(props) {
  const {
    allEntities,
    allOperators,
    meta,
    intl,
    context,
    currentModule,
    onAttributeSelect,
    onValueSearch,
    isNewBuilder,
    pickEntityFileds = false,
    fromReportBuilder = false,
    getValueGroupAccessor,
    getCustomGroupValueLabel,
    prefilledRule,
    onRulePrefilled,
    dependentFields
  } = props;
  let inputQuery;

  /**
   * Key and value may be different for different modules.
   * So we append/modify properties in a generic way for rule builder.
   */

  if (props.query) {
    inputQuery = appendProperties(props.query);
  }
  const preparedQueries = inputQuery || {
    id: `g-${nanoid()}`,
    rules: [],
    combinator: 'AND',
    not: false
  };
  // let preparedFields = [];
  const [query, setQuery] = useState(preparedQueries);
  const [fields, setFields] = useState([]);
  const [allEntityFieldsMap, setEntityFieldMap] = useState({});
  const operatorsFromMeta = allOperators.getRuleBuilderOperators.operators;

  /**
   * Translate operators from the received meta.
   */
  const processedOperators = produce(operatorsFromMeta, (draft) => {
    Object.keys(draft).forEach((key) => {
      draft[key] = getTranslatedText(intl, draft[key]);
    });
  });

  useEffect(() => {
    const parsedEntities = produce(allEntities, (draft) => {
      draft.getEntitiesMeta = draft.getEntitiesMeta.map((metaEntity) => {
        return {
          ...metaEntity,
          attributes: metaEntity.attributes.map((field) => {
            return {
              ...field,
              label: getTranslatedText(intl, field.label || field.name)
            };
          })
        };
      });
    });
    /**
     * conditionAttributes is obtained from specific module's meta.
     * It specifies which attributes from entities meta has to be displayed
     * in the attributes section of rule builder.
     */
    if (!isNewBuilder) {
      let preparedFields = [];
      preparedFields = parsedEntities
        ? getEntityAttributes(parsedEntities, ENTITY_MAP[currentModule]).filter(
            (item) => {
              return meta.conditionAttributes.some(
                (el) => el.name === item.name
              );
            }
          )
        : [];

      const entityFields = preparedFields.filter(
        (item) => item.dataType === 'entity'
      );

      const entityFieldSuggestedProperties = {};
      for (let i = 0; i < entityFields.length; i++) {
        /**
         * If the dataType is entity, we need to refer the entity meta of that entity
         * and select the fields which is specified in fields property of that conditionAttribute.
         */
        const conditionField = meta.conditionAttributes.find(
          (el) => el.name === entityFields[i].name
        );

        const allowedFieldsForEntity = conditionField.fields;
        const ca = [];
        const currentEntityAttributes = getEntityAttributes(
          parsedEntities,
          entityFields[i].entity
        );
        /**
         * We need to add the entity name as a attribute value if allowInCondition is true in meta for
         * entity datatype items
         */
        if (conditionField.allowInCondition) {
          const currentEntity = entityFields.find(
            (el) => el.name === conditionField.name
          );
          ca.push({
            value: currentEntity.name,
            label: getTranslatedText(intl, currentEntity.label),
            name: currentEntity.name
          });
        }
        for (let j = 0; j < allowedFieldsForEntity.length; j++) {
          const val = currentEntityAttributes.find((arr) => {
            return arr.name === allowedFieldsForEntity[j].name;
          });
          if (val) {
            ca.push({
              ...val,
              label: val.attributePlaceHolder
                ? getTranslatedText(intl, val.attributePlaceHolder.label)
                : '',
              name: `${entityFields[i].name}.${val.name}`
            });
          }
        }
        entityFieldSuggestedProperties[entityFields[i].name] = ca;
      }

      Object.keys(entityFieldSuggestedProperties).forEach((prop) => {
        preparedFields = preparedFields.concat(
          entityFieldSuggestedProperties[prop]
        );
      });
      /**
       * Take properties which are not entity type and have them as defaultModuleFields
       */
      entityFieldSuggestedProperties[currentModule] = getEntityAttributes(
        parsedEntities,
        ENTITY_MAP[currentModule]
      ).filter((item) => {
        return (
          meta.conditionAttributes.some((el) => el.name === item.name) &&
          item.dataType !== 'entity'
        );
      });
      setEntityFieldMap(entityFieldSuggestedProperties);
      setFields(preparedFields);
    } else {
      let allFields = [];
      let selectOptions = { [currentModule]: [] };
      const getRuleField = (attribute, entityToCheck, previousEntity = '') => {
        const pathName = previousEntity
          ? `${previousEntity}.${attribute.name}`
          : attribute.name;
        if (!attribute.fields) {
          const fieldEntry = getEntityAttributes(
            parsedEntities,
            entityToCheck
          ).find((attr) => attr.name === attribute.name);
          const formattedEntry = {
            ...fieldEntry,
            autoComplete: fieldEntry?.autoComplete,
            name: pathName,
            label: getTranslatedText(
              intl,
              fieldEntry?.attributePlaceHolder?.label || 'NA'
            )
          };
          allFields.push(formattedEntry);
          if (!previousEntity) {
            selectOptions[currentModule].push(formattedEntry);
          } else {
            if (!selectOptions[previousEntity]) {
              selectOptions[previousEntity] = [];
            }
            selectOptions[previousEntity].push(formattedEntry);
          }
        } else {
          const nestedEntityToCheck = getEntityAttributes(
            parsedEntities,
            entityToCheck
          )?.find((el) => el.name === attribute?.name)?.entity;
          attribute.fields.forEach((nestedConditionAttribute) => {
            getRuleField(
              nestedConditionAttribute,
              nestedEntityToCheck,
              pathName
            );
          });
        }
      };
      meta.conditionAttributes.forEach((conditionAttribute) => {
        getRuleField(conditionAttribute, ENTITY_MAP[currentModule], null);
      });
      console.log({ selectOptions, allFields });
      setEntityFieldMap(selectOptions);
      setFields(allFields);
    }
  }, [isNewBuilder, currentModule]);

  useEffect(() => {
    //adding the prefilled rule to the query
    if (prefilledRule && fields.length > 0) {
      const newRule = {
        id: `r-${nanoid()}`,
        field: prefilledRule.attribute,
        operator: prefilledRule.operator,
        value: prefilledRule.value || ''
      };

      const updatedQuery = produce(query, (draft) => {
        const targetIdx = prefilledRule.targetGroupIndex;
        const targetGroup = targetIdx >= 0 ? draft.rules[targetIdx] : undefined;

        if (targetGroup) {
          if (targetGroup.rules) {
            targetGroup.rules.push(newRule);
            if (prefilledRule.forceAndOperator) {
              targetGroup.combinator = 'AND';
              targetGroup.disabledCombinator = true;
            }
          }
        }
      });

      setQuery(updatedQuery);
      context.updateAutomationRule(
        formatQuery(updatedQuery, 'json'),
        'ruleCondition'
      );
      onRulePrefilled?.();
    }
  }, [prefilledRule, fields]);

  /**
   * This method is called whenever a change happens to the query
   * @param {object} updatedQuery
   */
  const handleQueryChange = (updatedQuery) => {
    setQuery(updatedQuery);
    const formattedRule = formatQuery(updatedQuery, 'json');
    context.updateAutomationRule(formattedRule, 'ruleCondition');
  };

  /**
   * Need to select what operators should be displayed for the selected attribute
   * @param {object} field
   */
  const getOperators = (field) => {
    const allFields = fields;
    const selectedField = allFields.find((el) => el.name === field);
    const operators = processedOperators;
    const operatorMap = allOperators.getRuleBuilderOperators.operatorType;
    if (
      selectedField &&
      Object.keys(selectedField).length &&
      selectedField.dataType !== 'entity'
    ) {
      const requiredOperators = pick(
        operators,
        operatorMap[selectedField.dataType]
      );
      let formattedOperatorValues = [];
      /**
       * If the operators is placeholders, we need to take the placeholders from the entity item's
       * condition placeholders available in the entities meta.
       */
      if (
        operatorMap?.[selectedField.dataType]?.findIndex(
          (el) => el === 'placeholders'
        ) > -1
      ) {
        const selectedFieldFromMeta = meta.conditionAttributes.find(
          (el) => el.name === selectedField.name
        );
        const metaPlaceholders = selectedFieldFromMeta.placeholders || [];
        formattedOperatorValues = metaPlaceholders.map((el) => {
          return {
            name: el,
            label: getTranslatedText(intl, el),
            type: 'placeholder'
          };
        });
        return formattedOperatorValues;
      }
      formattedOperatorValues = Object.keys(requiredOperators).map((prop) => {
        return {
          name: prop,
          label: requiredOperators[prop]
        };
      });
      return formattedOperatorValues;
    }
    if (selectedField?.dataType === 'entity') {
      let formattedOperatorValues = [];
      if (
        operatorMap?.[selectedField.dataType]?.findIndex(
          (el) => el === 'placeholders'
        ) > -1
      ) {
        const selectedFieldFromMeta = meta.conditionAttributes.find(
          (el) => el.name === selectedField.name
        );
        const metaPlaceholders = selectedFieldFromMeta.placeholders || [];
        formattedOperatorValues = metaPlaceholders.map((el) => {
          return {
            name: el,
            label: getTranslatedText(intl, el),
            type: 'placeholder'
          };
        });
        return formattedOperatorValues;
      }
    }
    return [];
  };

  /**
   * If there are fields which has to be grouped under a heading
   * we need to construct it in the below format.
   */
  const getGroupedFields = () => {
    const groupedFields = Object.keys(allEntityFieldsMap).map((prop) => {
      return {
        label: `${prop.split('.').join(' ')} fields`,
        options: allEntityFieldsMap[prop]
      };
    });
    return groupedFields;
  };

  /**
   * Return the component type which has to be rendered for selected attribute
   * @param {object} field
   * @param {String} operator
   */
  const getValueEditorType = (field, operator) => {
    const selectedField = fields.find((el) => el.name === field);
    if (selectedField) {
      if (!operator) return null;

      // Check for version dataType first to override default OPERATOR_COMPONENT_MAP
      if (selectedField?.dataType === 'version') {
        switch (operator) {
          case 'between':
            return 'dual_text_field';
          default:
            break;
        }
      }
      if (
        includes(
          ['text', 'read_only', 'numeric', 'decimal'],
          selectedField?.filterType
        )
      ) {
        if (selectedField.dataType === 'datetime') {
          switch (operator) {
            case 'after':
            case 'before':
              return 'datetime_picker';
            case 'between':
              return 'dual_datatime_picker';
            default:
              break;
          }
        }
        if (selectedField.dataType === 'boolean') {
          switch (operator) {
            case 'is':
              return 'boolean_select';
            default:
              break;
          }
        }
        if (selectedField.dataType === 'number_unit') {
          switch (operator) {
            case 'isEmpty':
            case 'isNotEmpty':
              return null;
            default:
              return 'unit';
          }
        }
        if (selectedField?.autoComplete) {
          switch (operator) {
            case 'is':
            case 'isNot':
              return 'search_select';
            case 'includes':
            case 'notIncludes':
              return 'multi_search_select';
            case 'isEmpty':
            case 'isNotEmpty':
              return null;
            case 'startsWith':
            case 'endsWith':
            case 'contains':
            case 'notContains':
              return 'text';
            default:
              return selectedField?.filterType || 'text';
          }
        }
        switch (operator) {
          case 'isEmpty':
          case 'isNotEmpty':
            return null;
          case 'on':
          case 'inNext':
          case 'inLast':
          case 'notInLast':
          case 'after':
          case 'before':
          case 'between':
            return OPERATOR_COMPONENT_MAP[operator];
          case 'includes':
          case 'notIncludes':
            return 'multi_select_text';
          default:
            return 'text';
        }
      }
      if (selectedField?.dataType === 'datetime') {
        switch (operator) {
          case 'after':
          case 'before':
            return 'datetime_picker';
          case 'between':
            return 'dual_datatime_picker';
          default:
            break;
        }
      }
      if (selectedField?.dataType === 'boolean') {
        switch (operator) {
          case 'is':
            return 'boolean_select';
          default:
            break;
        }
      }
      if (selectedField?.autoComplete) {
        switch (operator) {
          case 'is':
          case 'isNot':
            return 'search_select';
          case 'includes':
          case 'notIncludes':
            return 'multi_search_select';
          case 'isEmpty':
          case 'isNotEmpty':
            return null;
          case 'startsWith':
          case 'endsWith':
          case 'contains':
          case 'notContains':
            return 'text';
          default:
            return selectedField?.filterType || 'text';
        }
      }

      return OPERATOR_COMPONENT_MAP[operator];
    }
  };

  /**
   * For input value editor, we need to get the input type.
   * number, text, decimal etc.
   * @param {object} field
   * @param {String} operator
   */
  const getInputType = (field, operator) => {
    const selectedField = fields.find((el) => el.name === field);
    if (!operator) return null;
    if (includes(['numeric', 'decimal'], selectedField?.filterType)) {
      return 'number';
    }
    return OPERATOR_COMPONENT_MAP[operator];
  };

  return (
    <div className="flex-box-outer" name={props.name}>
      <div className="flex-box">
        <div className="scroll">
          <QueryBuilder
            query={query}
            fields={fields}
            isFieldGrouped
            onQueryChange={handleQueryChange}
            getOperators={getOperators}
            getGroupedFields={getGroupedFields}
            getValueEditorType={getValueEditorType}
            getInputType={getInputType}
            getValues={async function(item, operator) {
              const result = await onAttributeSelect({
                item,
                operator,
                fields,
                allOperators,
                intl,
                currentModule,
                fromReportBuilder: fromReportBuilder
              });
              return result;
            }}
            fetchValues={async function(client, query, params) {
              const result = await onValueSearch(client, query, params);
              return result;
            }}
            showCombinatorsBetweenRules
            showNotToggle={false}
            resetOnFieldChange
            operatorComponentMap={OPERATOR_COMPONENT_MAP}
            errors={props.errors}
            checkInactiveField={checkInactiveField({
              module: currentModule,
              allEntities: allEntities,
              allOperators: allOperators,
              intl: intl
            })}
            getValueGroupAccessor={getValueGroupAccessor}
            getCustomGroupValueLabel={getCustomGroupValueLabel}
            dependentFields={dependentFields}
          />
        </div>
      </div>
    </div>
  );
}

export default injectIntl(RuleBuilder);
